import React, { Component, ChangeEvent } from 'react';
import { Container, Row, Col } from 'reactstrap';
import InputWithLabel from '../common/InputWithLabel';
import { grossReceiptTypes } from '../../utility/Constants';
import { FoodBeverageReturnLocation, ReturnLocation, RoomOccupancyLocation, SplitVehicleLocation } from '../../types/LocationTypes';
import { filterMoneyInput, addZeroes } from '../../utility/InputFunctions';

interface LocationProps {
    type: grossReceiptTypes;
    index: number;
    location: ReturnLocation | FoodBeverageReturnLocation | SplitVehicleLocation;
    splitVehicleUnder?: number;
    splitVehicleOver?: number;
    updateGrossReceipts: (index: number, value: string) => void;
    updateNonPreparedReceipts: (index: number, value: string) => void;
    updateSplitVehicleReceipts: (index: number, value: string, isOver: boolean, otherValue: string | undefined) => void;
    updateRoomOccupancyReceipts: (index: number, field: string, value: string) => void;
}

export default class LocationInput extends Component<LocationProps> {

    isFoodBeverage = () => {
        return this.props.type === grossReceiptTypes.foodBev;
    }

    hasMultipleLocationFields = () => {
        return (this.props.type === grossReceiptTypes.vehicle && this.props.splitVehicleUnder !== undefined && this.props.splitVehicleUnder > 0) || this.props.type === grossReceiptTypes.roomOccupancy;
    }

    get grossReceiptText() {
        switch (this.props.type) {
            case grossReceiptTypes.heavyEquipment:
                return "Gross Receipts for Short-Term Heavy Equipment Rental (Excluding Sales Tax)";
            case grossReceiptTypes.vehicle:
                return "Gross Receipts for Short Term Rental of Vehicles";
            default:
                return "Gross Retail Receipts (Excluding Sales Tax)";
        }
    }

    updateGrossReceipts = (event: ChangeEvent<HTMLInputElement>, allowedDigitsBeforeDecimal: number, blur: boolean = false): void =>
        this.updateDecimalFields(event, allowedDigitsBeforeDecimal, (newValue) => this.props.updateGrossReceipts(this.props.index, newValue), blur);

    updateNonPreparedReceiptsCurrency = (event: ChangeEvent<HTMLInputElement>, allowedDigitsBeforeDecimal: number, blur: boolean = false): void =>
        this.updateDecimalFields(event, allowedDigitsBeforeDecimal, (newValue) => this.props.updateNonPreparedReceipts(this.props.index, newValue), blur);

    updateSplitVehicleReceipts = (event: ChangeEvent<HTMLInputElement>, allowedDigitsBeforeDecimal: number, otherValue: string | undefined, isOver: boolean, blur: boolean = false): void =>
        this.updateDecimalFields(event, allowedDigitsBeforeDecimal, (newValue) => this.props.updateSplitVehicleReceipts(this.props.index, newValue, isOver, otherValue), blur);

    updateRoomOccupancyReceipts = (event: ChangeEvent<HTMLInputElement>, allowedDigitsBeforeDecimal: number, field: string, index: number, blur: boolean = false): void =>        
        this.updateDecimalFields(event, allowedDigitsBeforeDecimal, (newValue) => this.props.updateRoomOccupancyReceipts(index, field, newValue), blur);
    

    updateDecimalFields = (event: ChangeEvent<HTMLInputElement>, allowedDigitsBeforeDecimal: number, updateState: (newValue: string) => void, blur: boolean) => {
        const newValue = blur ? addZeroes(event.target.value) : filterMoneyInput(event, allowedDigitsBeforeDecimal)
        if (newValue !== undefined) {
            updateState(newValue);
        }
    }

    render() {
        return (
            <Container className="white-background location-base">
                <Row>
                    <Col>
                        <span className="underline">Location Name</span>
                    </Col>
                    <Col>
                        <span className="underline">Address</span>
                    </Col>
                    <Col>
                        <span className="underline">Jurisdiction</span>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        {this.props.location.name}
                    </Col>
                    <Col>
                        {this.props.location.address}
                    </Col>
                    <Col>
                        {this.props.location.jurisdictionDescription}
                    </Col>
                </Row>
                {this.hasMultipleLocationFields() ?
                    <>

                        {this.props.type === grossReceiptTypes.roomOccupancy ?
                            <>
                                <Row>
                                    <Col className="padded-col">
                                        <InputWithLabel
                                            label={`${this.grossReceiptText} $`}
                                            type="text"
                                            name="grossReceipts"
                                            id={`grossReceipts${this.props.index}`}
                                            value={this.props.location.grossReceipts}
                                            onChange={e => this.updateGrossReceipts(e, 8)}
                                            onBlur={e => this.updateGrossReceipts(e, 8, true)}

                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col className="padded-col">
                                        <InputWithLabel
                                            label="Extended Stays $"
                                            type="text"
                                            name="extendedStays"
                                            id={`extendedStays${this.props.index}`}
                                            value={(this.props.location as RoomOccupancyLocation).extendedStays}
                                            onChange={e => this.updateRoomOccupancyReceipts(e, 8, 'extendedStays', this.props.index)}
                                            onBlur={e => this.updateRoomOccupancyReceipts(e, 8, 'extendedStays', this.props.index, true)}
                                        />
                                    </Col>
                                    <Col className="padded-col">
                                        <InputWithLabel
                                            label="Government Exemptions $"
                                            type="text"
                                            name="governmentExemptions"
                                            id={`governmentExemptions${this.props.index}`}
                                            value={(this.props.location as RoomOccupancyLocation).governmentExemptions}
                                            onChange={e => this.updateRoomOccupancyReceipts(e, 8, 'governmentExemptions', this.props.index)}
                                            onBlur={e => this.updateRoomOccupancyReceipts(e, 8, 'governmentExemptions', this.props.index, true)}
                                        />
                                    </Col>
                                    <Col className="padded-col">
                                        <InputWithLabel
                                            label="Non-Occupancy $"
                                            type="text"
                                            name="nonOccupancy"
                                            id={`nonOccupancy${this.props.index}`}
                                            value={(this.props.location as RoomOccupancyLocation).nonOccupancy}
                                            onChange={e => this.updateRoomOccupancyReceipts(e, 8, 'nonOccupancy', this.props.index)}
                                            onBlur={e => this.updateRoomOccupancyReceipts(e, 8, 'nonOccupancy', this.props.index, true)}
                                        />
                                    </Col>
                                </Row>
                            </>
                            :
                            <>
                                <Row>
                                    <Col>
                                        <span className="underline">{this.grossReceiptText}</span>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col className="padded-col">
                                        <InputWithLabel
                                            label={`Under ${this.props.splitVehicleUnder?.toLocaleString()} lbs $`}
                                            type="text"
                                            name="grossReceiptsUnder"
                                            id={`grossReceiptsUnder${this.props.index}`}
                                            value={(this.props.location as SplitVehicleLocation).grossReceiptsUnder}
                                            onChange={e => this.updateSplitVehicleReceipts(e, 8, (this.props.location as SplitVehicleLocation).grossReceiptsOver, false)}
                                            onBlur={e => this.updateSplitVehicleReceipts(e, 8, (this.props.location as SplitVehicleLocation).grossReceiptsOver, false, true)}
                                        />
                                    </Col>
                                    <Col className="padded-col">
                                        <InputWithLabel
                                            label={
                                                this.props.splitVehicleOver
                                                    ? `${this.props.splitVehicleUnder?.toLocaleString()} - ${this.props.splitVehicleOver?.toLocaleString()} lbs $`
                                                    : `Over ${this.props.splitVehicleUnder?.toLocaleString()} lbs $`
                                            }
                                            type="text"
                                            name="grossReceiptsOver"
                                            id={`grossReceiptsOver${this.props.index}`}
                                            value={(this.props.location as SplitVehicleLocation).grossReceiptsOver}
                                            onChange={e => this.updateSplitVehicleReceipts(e, 8, (this.props.location as SplitVehicleLocation).grossReceiptsUnder, true)}
                                            onBlur={e => this.updateSplitVehicleReceipts(e, 8, (this.props.location as SplitVehicleLocation).grossReceiptsUnder, true, true)}

                                        />
                                    </Col>
                                </Row>
                            </>
                        }
                    </>
                    :
                    <Row>
                        <Col className="padded-col">
                            <InputWithLabel
                                label={`${this.grossReceiptText} $`}
                                type="text"
                                name="grossReceipts"
                                id={`grossReceipts${this.props.index}`}
                                value={this.props.location.grossReceipts}
                                onChange={e => this.updateGrossReceipts(e, 8)}
                                onBlur={e => this.updateGrossReceipts(e, 8, true)}

                            />
                        </Col>
                        {
                            this.isFoodBeverage() ?
                                <Col className="padded-col">
                                    <InputWithLabel
                                        label="Non-Prepared Food/Beverage Receipts $"
                                        type="text"
                                        name="nonPrepared"
                                        id={`nonPrepared${this.props.index}`}
                                        value={(this.props.location as FoodBeverageReturnLocation).nonPreparedFoodBeverageReceipts ?? ''}
                                        onChange={e => this.updateNonPreparedReceiptsCurrency(e, 8)}
                                        onBlur={e => this.updateNonPreparedReceiptsCurrency(e, 8, true)}
                                    />
                                </Col> :
                                <></>
                        }
                    </Row>
                }
            </Container>
        );
    }
}